import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BrowserStorageService } from './browser-storage.service';
import { Model } from '../../../api-gen/gen/models/model';
import { DetailLevel } from '../../../api-gen/gen/models/detail-level';
import { EditModel } from '../../../api-gen/gen/models/edit-model';

export type ViewMode = 'list' | 'grid';

export interface UserSettings {
  infiniteScrollEnabled: boolean;
  defaultViewMode: ViewMode;
  lightTheme: boolean;
  defaultImageModel: Model;
  defaultDetailLevel: DetailLevel;
  defaultChatImageModel: EditModel;
  showReplySuggestions: boolean;
}

const DEFAULT_SETTINGS: UserSettings = {
  infiniteScrollEnabled: true,
  defaultViewMode: 'grid',
  lightTheme: true,
  defaultImageModel: Model.Default,
  defaultDetailLevel: DetailLevel.Medium,
  defaultChatImageModel: EditModel.Creative,
  showReplySuggestions: true,
};

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private readonly STORAGE_KEY = 'user_settings';
  private storage = inject(BrowserStorageService);
  private settings: BehaviorSubject<UserSettings>;

  constructor() {
    const savedSettings = this.loadSettings();
    this.settings = new BehaviorSubject<UserSettings>(savedSettings);
  }

  private loadSettings(): UserSettings {
    const savedSettings = this.storage.getItem(this.STORAGE_KEY);
    if (savedSettings) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(savedSettings) };
    }
    return DEFAULT_SETTINGS;
  }

  private saveSettings(settings: UserSettings): void {
    this.storage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
    this.settings.next(settings);
  }

  getSettings(): Observable<UserSettings> {
    return this.settings.asObservable();
  }

  getCurrentSettings(): UserSettings {
    return this.settings.value;
  }

  updateSettings(settings: Partial<UserSettings>): void {
    const updatedSettings = {
      ...this.settings.value,
      ...settings,
    };
    this.saveSettings(updatedSettings);
  }
}
